import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Logo } from '@aosenuma/ui';
import { perfilMarca, propagarMarca } from '@aosenuma/lib-types';
import { getSupabase, isSupabaseConfigured, bffFetchPublic, BffError } from '@aosenuma/lib-supabase';
import { isAllowedAuthEmail, parseAllowedDomains } from '@aosenuma/lib-auth';

type Rol = 'cliente' | 'tecnico';

const URL_DASHBOARD = import.meta.env.VITE_PUBLIC_URL_DASHBOARD ?? 'http://localhost:5174';
const URL_EQUIPO = import.meta.env.VITE_PUBLIC_URL_EQUIPO ?? 'http://localhost:5175';

if (import.meta.env.PROD) {
  if (!import.meta.env.VITE_PUBLIC_URL_DASHBOARD) {
    throw new Error('VITE_PUBLIC_URL_DASHBOARD requerido en producción');
  }
  if (!import.meta.env.VITE_PUBLIC_URL_EQUIPO) {
    throw new Error('VITE_PUBLIC_URL_EQUIPO requerido en producción');
  }
}
/**
 * Whitelist de dominios para magic-link / password (defensa cliente, A4).
 * CSV en `VITE_PUBLIC_ALLOWED_AUTH_DOMAINS`. Vacío en dev = permite todo.
 * En prod debe estar seteado a "elawan.com,aosenuma.com,aosenumareport.com".
 */
const ALLOWED_AUTH_DOMAINS_RAW = import.meta.env.VITE_PUBLIC_ALLOWED_AUTH_DOMAINS as string | undefined;

/**
 * Throttle anti brute-force (threat model C): tras un submit exitoso, bloquea
 * nuevos intentos por 3s del lado cliente. Supabase tiene su propio rate
 * limit por email, pero esto evita spam ANTES de hacer la red round-trip.
 * Ojo: solo es defensa cliente; un atacante puede saltarse el state. Por eso
 * el rate limit real vive en Cloudflare WAF + Supabase.
 */
const SUBMIT_COOLDOWN_MS = 3000;

export function AccessCard() {
  const [rol, setRol] = useState<Rol>('cliente');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);
  const cooldownEndRef = useRef<number>(0);

  /** Whitelist parseada una sola vez por mount. */
  const allowedDomains = useMemo(() => parseAllowedDomains(ALLOWED_AUTH_DOMAINS_RAW), []);

  // Tick del cooldown: actualiza UI cada 250ms mientras hay timer activo.
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const interval = window.setInterval(() => {
      const left = Math.max(0, cooldownEndRef.current - Date.now());
      setCooldownLeft(left);
      if (left <= 0) window.clearInterval(interval);
    }, 250);
    return () => window.clearInterval(interval);
  }, [cooldownLeft]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMagicSent(false);

    if (!isSupabaseConfigured()) {
      setError('Supabase no configurado. Revisa .env.local.');
      return;
    }

    // Validación cliente-side: consulta dinámica a allowed_emails via el
    // BFF /v1/auth/check-email (api-edge), que envuelve la RPC
    // is_email_authorized_for_login con rate limit por IP (10 req/min).
    // Antes el FE llamaba al RPC anon directo, vulnerable a email
    // enumeration. Ahora el rate limit del Worker hace que enumerar un
    // dominio tome semanas.
    //
    // Triple fallback:
    //   1. BFF check-email (canónico).
    //   2. 429 (rate limited) → mensaje claro "intenta en X segundos",
    //      no caer al fallback (eso defeats el rate limit).
    //   3. Cualquier otro error de red → cae al env var hardcoded
    //      (degraded pero no bloquea durante un outage del Worker).
    //
    // El hook server-side validate_email_domain_signup sigue siendo el
    // gate REAL — esta validación es solo UX para evitar OTPs perdidos.
    try {
      const { authorized } = await bffFetchPublic<{ authorized: boolean }>(
        '/v1/auth/check-email',
        { method: 'POST', body: { email } },
      );
      if (!authorized) {
        setError('Este correo no está autorizado. Pide al administrador que te invite primero.');
        cooldownEndRef.current = Date.now() + SUBMIT_COOLDOWN_MS;
        setCooldownLeft(SUBMIT_COOLDOWN_MS);
        return;
      }
    } catch (err) {
      if (err instanceof BffError && err.status === 429) {
        setError('Demasiados intentos. Espera 1 minuto e intenta de nuevo.');
        cooldownEndRef.current = Date.now() + SUBMIT_COOLDOWN_MS;
        setCooldownLeft(SUBMIT_COOLDOWN_MS);
        return;
      }
      // BFF down / red caída → fallback al env var. Si tampoco está,
      // dejamos pasar al server (el hook lo rechazará si no autorizado).
      if (allowedDomains.length > 0 && !isAllowedAuthEmail(email, allowedDomains)) {
        setError('Este correo no está autorizado para acceder al panel.');
        cooldownEndRef.current = Date.now() + SUBMIT_COOLDOWN_MS;
        setCooldownLeft(SUBMIT_COOLDOWN_MS);
        return;
      }
    }

    if (Date.now() < cooldownEndRef.current) {
      // Defensa contra double-click + bots simples. El boton tambien va disabled.
      return;
    }

    setBusy(true);
    try {
      const sb = getSupabase();
      const dest = rol === 'cliente' ? URL_DASHBOARD : URL_EQUIPO;

      if (password.trim().length > 0) {
        // Password flow.
        const { data, error: err } = await sb.auth.signInWithPassword({ email, password });
        if (err) throw err;
        // El user.app_metadata del response NO incluye rol_app (los claims del
        // custom_access_token_hook viven solo en el JWT). Decodificamos el
        // access_token para resolver el subdomain destino.
        const realRol = readRolFromJwt(data.session?.access_token);
        const target =
          realRol === 'cliente_elawan' ? URL_DASHBOARD :
          realRol === 'admin_aosenuma' || realRol === 'tecnico_aosenuma' ? URL_EQUIPO :
          dest;
        // Cross-subdomain: localStorage NO se comparte entre elawan.* y
        // elawan-dashboard.*/elawan-equipo.*. Redirigimos a /auth/callback
        // del destino con tokens en hash; AuthCallback del subdomain hace
        // setSession en SU localStorage. Mismo flujo que magic link.
        // `propagarMarca` es no-op salvo en modo presentación. localStorage no
        // cruza subdominios, así que el modo viaja en la URL o no llega.
        const redirect = propagarMarca(appendTokensIfCrossOrigin(target, data.session));
        window.location.href = redirect;
      } else {
        // Magic link flow (default — los users invitados llegan aquí primero)
        const { error: err } = await sb.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: propagarMarca(`${dest}/auth/callback`) },
        });
        if (err) throw err;
        setMagicSent(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error inesperado');
    } finally {
      setBusy(false);
      // Inicia cooldown al final, exitoso o fallido — un atacante no debe poder
      // distinguir éxito vs fallo por la latencia del cooldown.
      cooldownEndRef.current = Date.now() + SUBMIT_COOLDOWN_MS;
      setCooldownLeft(SUBMIT_COOLDOWN_MS);
    }
  }

  const marca = perfilMarca();

  return (
    <div className="access-card">
      <div className="client-row">
        {marca.neutral ? (
          /* El hueco del cliente lo ocupa la marca propia — assets que el
             monorepo ya trae, sin inventar un logo de relleno. */
          <Logo
            variant="aosenuma-logo-color"
            height={74}
            alt="aosenuma report"
            className="client-logo client-logo--neutra"
          />
        ) : (
          <Logo variant="elawan-color-login" height={170} className="client-logo" />
        )}
        {!marca.ocultarPoweredBy && (
          <div className="powered-by-card">
            <span>powered by</span>
            <Logo variant="aosenuma-icon-white" height={14} />
            <span className="ao-name">aosenuma</span>
          </div>
        )}
      </div>

      <div className="access-title">Acceso al panel</div>
      <p className="access-sub">
        Selecciona tu rol e inicia sesión con tu correo institucional.
      </p>

      <div className="role-toggle" role="tablist">
        <button
          type="button"
          className={rol === 'cliente' ? 'active' : ''}
          onClick={() => setRol('cliente')}
          role="tab"
          aria-selected={rol === 'cliente'}
        >
          {marca.etiquetaRolCliente}
        </button>
        <button
          type="button"
          className={rol === 'tecnico' ? 'active' : ''}
          onClick={() => setRol('tecnico')}
          role="tab"
          aria-selected={rol === 'tecnico'}
        >
          Técnico · aosenuma
        </button>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label htmlFor="email">Correo</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder={rol === 'cliente' ? marca.correoEjemplo : 'nombre@aosenuma.com'}
            inputMode="email"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="password">
            Contraseña <span style={{ opacity: 0.6, fontSize: '0.85em' }}>(opcional = magic link)</span>
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <div className="auth-error" style={{ color: '#c0392b', marginTop: 8 }}>{error}</div>}
        {magicSent && (
          <div className="auth-success" style={{ color: '#27ae60', marginTop: 8 }}>
            Te enviamos un magic link a <strong>{email}</strong>. Revisa tu correo.
          </div>
        )}

        <button
          type="submit"
          className="submit-btn"
          disabled={busy || !email || cooldownLeft > 0}
        >
          {busy
            ? 'Verificando…'
            : cooldownLeft > 0
              ? `Espera ${Math.ceil(cooldownLeft / 1000)}s…`
              : password
                ? 'Acceder al panel'
                : 'Enviar magic link'}
        </button>
      </form>

      <div className="meta">
        <span>Magic-link disponible</span>
        {/* El asunto se ve en la barra de estado al pasar el cursor. */}
        <a href={`mailto:sarriaga@aosenuma.com?subject=${encodeURIComponent(marca.asuntoAyuda)}`}>
          ¿Necesitas ayuda?
        </a>
      </div>
    </div>
  );
}

/** Decodifica `payload.app_metadata.rol_app` del JWT (sin verificar firma). */
function readRolFromJwt(token: string | undefined | null): string | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { app_metadata?: { rol_app?: string } };
    return payload.app_metadata?.rol_app ?? null;
  } catch {
    return null;
  }
}

/**
 * Cross-subdomain handoff: si `target` apunta a otro origin (landing →
 * dashboard/equipo), aterriza en `/auth/callback` del destino con los tokens
 * en el hash. AuthCallback del destino los detecta y hace setSession en SU
 * localStorage. Mismo origin: passthrough.
 *
 * Seguridad: el hash NO se envía al server (no aparece en logs HTTP); una
 * vez setSession, AuthCallback limpia el hash con replaceState.
 */
function appendTokensIfCrossOrigin(
  target: string,
  session: { access_token: string; refresh_token: string } | null | undefined,
): string {
  if (!session) return target;
  if (typeof window === 'undefined') return target;
  if (target.startsWith('/') && !target.startsWith('//')) return target;
  let parsed: URL;
  try {
    parsed = new URL(target, window.location.origin);
  } catch {
    return target;
  }
  if (parsed.origin === window.location.origin) return parsed.toString();
  if (!parsed.pathname.endsWith('/auth/callback')) {
    parsed.pathname = parsed.pathname.replace(/\/$/, '') + '/auth/callback';
  }
  const hashParams = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    type: 'bearer',
  });
  parsed.hash = '#' + hashParams.toString();
  return parsed.toString();
}
